import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { store } from "../app/store";
import { _post } from "../api/apiClient";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { CustomSuccess } from "../api/CustomSuccess";
import {
  setLoaderFalse,
  setLoaderTrue,
  setPerformaLoading,
} from "./loaderSlice";
import { current } from "immer";
import { getGraphObjectsFromInitialFilter } from "./newInitialFilterDataSlice";
import {
  setApplyButtonClicked,
  setEnableApplyButton,
} from "./selectedFilterSlice";
import { API_STATUS } from "../rtk-query/features/sliceUtil";

const createSelector = (getter) => () => getter(store.getState());

export const _orgClientId = createSelector(
  (state) => state?.orgReducer?.selectedFI?.orgClientId,
);
export const _hid = createSelector(
  (state) => state?.orgReducer?.healthAnalysisID?.uid,
);
export const _FILTERS = createSelector(
  (state) => state?.newClusterReducer?.FILTERS,
);
export const _contextualFilterObj = createSelector(
  (state) => state?.newClusterReducer?.contextualFilterObj,
);
export const _userMailId = createSelector(
  (state) => state?.authReducer?.userData?.userName,
);
export const _currentDashboard = createSelector(
  (state) => state.dashboardReducer.currentDashboard,
);
export const _clusterApiKey = createSelector(
  (state) =>
    state?.dashboardReducer?.selectedCard?.clusterObject?.clusterApiKey,
);
export const _selectedCard = createSelector(
  (state) => state?.dashboardReducer?.selectedCard,
);

export const SORT_BY = {
  alphabetical: "alphabetical",
  numerical: "numerical",
};

export const SORT_ORDER = {
  ascending: "ascending",
  descending: "descending",
};

export const FILTER_KEYS = {
  balance: "balance",
  oppotype: "oppotype",
  deposit_option: "deposit_option",
  products: "products",
  product_with_provider: "product_with_provider",
  consumer: "consumer",
  persona: "persona",
  zonename: "zonename",
  consumertype: "consumertype",
  deposit_type: "deposit_type",
  depositType_with_provider: "product_with_provider",
  providers: "providers",
  category: "category",
  category_with_merchant: "category_with_merchant",
  merchant: "merchant",
};

const NUMERIC_DESC_SORT = {
  sortBy: SORT_BY.numerical,
  sortOrder: SORT_ORDER.descending,
};

const ALPHA_ASC_SORT = {
  sortBy: SORT_BY.alphabetical,
  sortOrder: SORT_ORDER.ascending,
};

const createBalanceFilter = (filterName) => ({
  key: FILTER_KEYS.balance,
  filterName,
});

const createSortedFilter = (key, filterName, sortConfig) => ({
  key,
  filterName,
  ...sortConfig,
});

const createSubFilterObject = (config, defaults = {}) => ({
  key: config.key,
  filterName: config.filterName,
  postFix: config.postFix,
  subFilterName: config.subFilterName,
  subFilterKey: config.subFilterKey,
  subFilterInternalKey: config.subFilterInternalKey,
  subFilterDisplayKey: config.subFilterDisplayKey || "name",
  sortBy: config.sortBy,
  sortOrder: config.sortOrder,
  subFilterSortBy: config.subFilterSortBy,
  subFilterSortOrder: config.subFilterSortOrder,
  extendMenu: false,
  ...defaults,
});

const createProductsWithProvidersFilter = ({
  key = FILTER_KEYS.products,
  filterName = "Products",
  postFix = "Providers",
  subFilterName = FILTER_KEYS.product_with_provider,
  subFilterKey = FILTER_KEYS.product_with_provider,
  subFilterInternalKey = "product",
  subFilterDisplayKey = "name",
  sortBy = SORT_BY.numerical,
  sortOrder = SORT_ORDER.descending,
  subFilterSortBy = SORT_BY.numerical,
  subFilterSortOrder = SORT_ORDER.descending,
} = {}) =>
  createSubFilterObject({
    key,
    filterName,
    postFix,
    subFilterName,
    subFilterKey,
    subFilterInternalKey,
    subFilterDisplayKey,
    sortBy,
    sortOrder,
    subFilterSortBy,
    subFilterSortOrder,
  });

const createCategoryWithEntityFilter = ({ postFix = "Merchants" } = {}) =>
  createSubFilterObject({
    key: FILTER_KEYS.category,
    filterName: "Category",
    postFix,
    subFilterName: FILTER_KEYS.category_with_merchant,
    subFilterKey: FILTER_KEYS.category_with_merchant,
    subFilterInternalKey: "category",
    subFilterDisplayKey: "name",
    sortBy: SORT_BY.numerical,
    sortOrder: SORT_ORDER.descending,
    subFilterSortBy: SORT_BY.numerical,
    subFilterSortOrder: SORT_ORDER.descending,
  });

const createDepositTypeWithProvidersFilter = () =>
  createSubFilterObject({
    key: FILTER_KEYS.deposit_type,
    filterName: "External Transfers",
    postFix: "Providers",
    subFilterName: FILTER_KEYS.depositType_with_provider,
    subFilterKey: FILTER_KEYS.depositType_with_provider,
    subFilterInternalKey: "deposit",
    subFilterDisplayKey: "name",
    sortBy: SORT_BY.numerical,
    sortOrder: SORT_ORDER.descending,
    subFilterSortBy: SORT_BY.numerical,
    subFilterSortOrder: SORT_ORDER.descending,
  });

const createAudienceFilters = (sortConfig) => [
  {
    key: FILTER_KEYS.consumer,
    filterName: "Consumers",
    ...sortConfig,
  },
  {
    key: FILTER_KEYS.persona,
    filterName: "Personas",
    ...sortConfig,
  },
  {
    key: FILTER_KEYS.zonename,
    filterName: "Zones",
    ...sortConfig,
  },
];

const createNumericAudienceFilters = () =>
  createAudienceFilters(NUMERIC_DESC_SORT);
const createAlphabeticalAudienceFilters = () =>
  createAudienceFilters(ALPHA_ASC_SORT);

const createGraphRequestConfig = (
  successMessage = "Fetched Graph data successfully.",
  errorMessage = "Failed to fetch Graph data!",
) => ({ successMessage, errorMessage });

const buildDrilldownUrl = (clusterApiKey, endpoint) =>
  `bank/${_orgClientId()}/drilldown/${_hid()}/${clusterApiKey}/${endpoint}`;

const createIndexMap = (
  items,
  keyExtractor = (item) => item?.id,
  valueMapper = (item) => item,
) => new Map(items?.map((item) => [keyExtractor(item), valueMapper(item)]));

const resetApplyButtonState = (thunkAPI) => {
  thunkAPI.dispatch(setEnableApplyButton(false));
  thunkAPI.dispatch(setApplyButtonClicked(false));
};

const buildDetailQueryString = (paginationObj = {}) => {
  const { currentPage, limit, sortObj } = paginationObj;
  const params = [];

  if (currentPage) params.push(`PageNumber=${currentPage}`);
  if (limit > 0) params.push(`PageSize=${limit}`);
  if (sortObj?.sortOrder) params.push(`OrderBy=${sortObj.sortOrder}`);
  if (sortObj?.sortField) params.push(`SortBy=${sortObj.sortField}`);

  return params.length > 0 ? `?${params.join("&")}` : "?";
};

const buildDrilldownPayload = ({
  filter = getOnlyIdsForAPI(),
  extra = {},
} = {}) => ({
  filter,
  requid: _hid(),
  producttype: _currentDashboard()?.KEY,
  gte: "",
  lte: "",
  duration: "",
  ...extra,
});

const parseDrilldownSuccessResponse = (response, config) => {
  if (response?.statusCode !== HttpStatusCode.Ok) {
    throw CustomError(response, config);
  }

  return CustomSuccess(response, {
    ...config,
    disableToast: true,
  });
};

const postDrilldownAndParse = async ({
  clusterApiKey,
  endpoint,
  payload,
  config,
}) => {
  const response = await _post(
    buildDrilldownUrl(clusterApiKey, endpoint),
    payload,
  );
  return parseDrilldownSuccessResponse(response, config);
};

const runDrilldownRequest = async ({ endpoint, payload, config }) => {
  const clusterApiKey = _clusterApiKey();
  const successResult = await postDrilldownAndParse({
    clusterApiKey,
    endpoint,
    payload,
    config,
  });

  return {
    clusterApiKey,
    successResult,
  };
};

const withLoadingThunkExecution = async (
  thunkAPI,
  { setLoadingTrue, setLoadingFalse, request, onSuccess = (result) => result },
) => {
  try {
    thunkAPI.dispatch(setLoadingTrue());
    const result = await request();
    thunkAPI.dispatch(setLoadingFalse());
    return onSuccess(result);
  } catch (error) {
    thunkAPI.dispatch(setLoadingFalse());
    return thunkAPI.rejectWithValue(error);
  }
};

const createThunkWithLoader = (
  type,
  request,
  { setLoadingTrue = setLoaderTrue, setLoadingFalse = setLoaderFalse } = {},
) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    return withLoadingThunkExecution(thunkAPI, {
      setLoadingTrue,
      setLoadingFalse,
      request: () => request(arg, thunkAPI),
    });
  });

const setStateLoadingStatus = (state, key, isLoading, data = null) => {
  state[key] = {
    ...(data !== null && { data }),
    isLoading,
  };
};

const setLoadingState = (state, key) => setStateLoadingStatus(state, key, true);
const setLoadedState = (state, key, data) =>
  setStateLoadingStatus(state, key, false, data);
const setLoadFailedState = (state, key) =>
  setStateLoadingStatus(state, key, false);

const mapFilterEntries = (value, mapper) =>
  Array.isArray(value)
    ? value.map((entry, index, collection) => mapper(entry, index, collection))
    : undefined;

const mapAndAssignFilterGroup = (state, groupKey, mapper) => {
  const existingArray = current(state.FILTERS) || [];
  const updatedArray = existingArray?.[groupKey]?.map((item) => mapper(item));

  state.FILTERS = {
    ...state.FILTERS,
    [groupKey]: updatedArray,
  };
};

const createMappedEntry = (entry, config, extra = {}) => ({
  ...entry,
  childOf: config?.key,
  ...extra,
});

const addBasicThunkCases = (builder, thunk, stateKey, onFulfilled = null) => {
  builder
    .addCase(thunk.pending, (state) => {
      setLoadingState(state, stateKey);
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (onFulfilled) {
        onFulfilled(state, action);
      } else {
        setLoadedState(state, stateKey, action.payload?.data);
      }
    })
    .addCase(thunk.rejected, (state) => {
      setLoadFailedState(state, stateKey);
    });
};

const createMerchantLikeFilters = (
  balanceLabel,
  categoryPostFix,
  merchantLabel,
) => [
  createBalanceFilter(balanceLabel),
  createCategoryWithEntityFilter({ postFix: categoryPostFix }),
  createSortedFilter(FILTER_KEYS.merchant, merchantLabel, NUMERIC_DESC_SORT),
  ...createNumericAudienceFilters(),
];

const initialState = {
  graphData: null,
  detailData: null,
  filterData: null,
  initialFilterData: {
    data: null,
    isLoading: true,
  },
  selectedFilterData: {},
  contextualFilterObj: null,
  FILTERS: {
    opportunities: [
      createBalanceFilter("Checking Account Balance"),
      createSortedFilter(
        FILTER_KEYS.oppotype,
        "Opportunity Types",
        NUMERIC_DESC_SORT,
      ),
      createProductsWithProvidersFilter(),
      ...createNumericAudienceFilters(),
    ],
    secondary_consumer: [
      createBalanceFilter("Checking Account Balance"),
      createSortedFilter(
        FILTER_KEYS.deposit_option,
        "Deposit Options",
        NUMERIC_DESC_SORT,
      ),
      createDepositTypeWithProvidersFilter(),
      createSortedFilter(
        FILTER_KEYS.providers,
        "Financial Institutions",
        NUMERIC_DESC_SORT,
      ),
      createSortedFilter(
        FILTER_KEYS.consumertype,
        "Consumers",
        NUMERIC_DESC_SORT,
      ),
      createSortedFilter(FILTER_KEYS.persona, "Personas", NUMERIC_DESC_SORT),
      createSortedFilter(FILTER_KEYS.zonename, "Zones", NUMERIC_DESC_SORT),
    ],
    ghost_consumer: [
      createBalanceFilter("Checking Account Balance"),
      createProductsWithProvidersFilter({
        sortBy: SORT_BY.numerical,
        sortOrder: SORT_ORDER.descending,
        subFilterSortBy: SORT_BY.numerical,
        subFilterSortOrder: SORT_ORDER.descending,
      }),
      ...createNumericAudienceFilters(),
    ],
    top_merchant: createMerchantLikeFilters(
      "Expenditure Amount",
      "Merchants",
      "Merchants",
    ),
    top_vendor: createMerchantLikeFilters(
      "Expenditure Amount",
      "Vendors",
      "Vendors",
    ),
    banking_services: [
      createBalanceFilter("Checking Account Balance"),
      createProductsWithProvidersFilter(),
      createSortedFilter(FILTER_KEYS.providers, "Merchants", NUMERIC_DESC_SORT),
      ...createNumericAudienceFilters(),
    ],
  },
  selectedZones: null,
  selectedFilterObject: null,
  isOnlyPredictive: false,
};

const createClusterObject = (
  key,
  defaultFilter,
  legendsKey,
  initialStateFilters,
  clusterApiKey = key,
) => ({
  key,
  clusterApiKey,
  filterGroup: initialStateFilters?.[key],
  defaultFilter,
  legendsKey,
  zonesKey: FILTER_KEYS.zonename,
});

export const CLUSTER_OBJECTS = {
  opportunities: createClusterObject(
    "opportunities",
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    "opportunities",
  ),
  secondary_consumer: createClusterObject(
    "secondary_consumer",
    FILTER_KEYS.deposit_type,
    FILTER_KEYS.deposit_type,
    initialState.FILTERS,
    "secondary_consumer",
  ),
  ghost_consumer: createClusterObject(
    "ghost_consumer",
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    "ghost_consumer",
  ),
  top_merchant: createClusterObject(
    "top_merchant",
    FILTER_KEYS.merchant,
    FILTER_KEYS.category,
    initialState.FILTERS,
    "top_merchant",
  ),
  top_vendor: createClusterObject(
    "top_vendor",
    FILTER_KEYS.merchant,
    FILTER_KEYS.category,
    initialState.FILTERS,
    "top_merchant",
  ),
  banking_services: createClusterObject(
    "banking_services",
    FILTER_KEYS.products,
    FILTER_KEYS.products,
    initialState.FILTERS,
    "banking_services",
  ),
};

export const getOnlyIdsForAPI = () => {
  const state = store.getState();
  const selectedFilterData = state?.newClusterReducer?.selectedFilterData;

  return Object.fromEntries(
    Object.entries(selectedFilterData || {})
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.map((item) => item.id) : value,
      ])
      .filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : typeof v === "object" && v !== null,
      ),
  );
};

function mapSimpleFilterValues(value, config) {
  return mapFilterEntries(value, (entry) => createMappedEntry(entry, config));
}

function mapNestedSubFilterValues(children, parentName, config) {
  return mapFilterEntries(children, (child) => ({
    ...child,
    childOf: parentName,
    rootFilter: config?.key,
    subFilterKey: config?.subFilterKey,
    subFilterDisplayKey: config?.subFilterDisplayKey,
  }));
}

function mapSubFilterValues(value, config) {
  return mapFilterEntries(value, (entry) =>
    createMappedEntry(entry, config, {
      postFix: config?.postFix,
      subFilterKey: config?.subFilterKey,
      subFilterName: config?.subFilterName,
      subFilterInternalKey: config?.subFilterInternalKey,
      extendMenu: false,
      subFilterSortBy: config?.subFilterSortBy,
      subFilterSortOrder: config?.subFilterSortOrder,
      [config?.subFilterKey]: mapNestedSubFilterValues(
        entry[config?.subFilterKey],
        entry?.name,
        config,
      ),
    }),
  );
}

export const transformFilterData = (inputArray, options = {}) => {
  const { filters = _FILTERS(), clusterApiKey = _clusterApiKey() } = options;
  const output = {};

  const processFilterKey = (key, value, config) => {
    if (key === FILTER_KEYS.balance) {
      return { ...config, data: value[0] };
    }
    if (config?.subFilterKey == null) {
      return { ...config, data: mapSimpleFilterValues(value, config) };
    }
    return { ...config, data: mapSubFilterValues(value, config) };
  };

  for (const sourceItem of inputArray) {
    if (!sourceItem || typeof sourceItem !== "object") {
      continue;
    }

    for (const [key, value] of Object.entries(sourceItem)) {
      const config = filters?.[clusterApiKey]?.find(
        (filterItem) => filterItem.key === key,
      );
      output[key] = processFilterKey(key, value, config);
    }
  }

  return output;
};

export const transformGraphData = async (data) => {
  if (!data?.[0]) return null;

  const { graphItems, zoneItems } = await getGraphObjectsFromInitialFilter();

  const graphMap = createIndexMap(
    graphItems?.data,
    ({ id }) => id,
    ({ id, name, color_code }) => ({ id, name, color: color_code }),
  );

  const zoneMap = createIndexMap(zoneItems?.data, (item) => item?.id);

  const transformZoneData = (zone) => {
    const updatedZoneData = (zone.data || []).map((item) => ({
      ...item,
      ...graphMap.get(item?.id),
      value: item?.count,
    }));

    return {
      ...zone,
      ...zoneMap.get(zone?.zone_id),
      data: updatedZoneData,
    };
  };

  return {
    ...data[0],
    zone: (data[0].zone || []).map(transformZoneData),
  };
};

export const fetchFilterApiThunk = createThunkWithLoader(
  "fetch_filter_api_thunk",
  async () => {
    const config = createGraphRequestConfig();
    const { clusterApiKey, successResult } = await runDrilldownRequest({
      endpoint: "filter",
      payload: buildDrilldownPayload({ filter: {} }),
      config,
    });

    return {
      clusterApiKey,
      ...successResult,
    };
  },
);

export const fetchGraphApiThunk = createThunkWithLoader(
  "fetch_graph_api_thunk",
  async (_, thunkAPI) => {
    const config = createGraphRequestConfig();
    const { successResult } = await runDrilldownRequest({
      endpoint: "graph",
      payload: buildDrilldownPayload({
        extra: {
          Is_context: _contextualFilterObj() != null,
        },
      }),
      config,
    });

    const graphData = await transformGraphData(successResult?.data);

    resetApplyButtonState(thunkAPI);
    return {
      ...successResult,
      data: graphData,
    };
  },
);

export const fetchDetailApiThunk = createThunkWithLoader(
  "fetch_detail_api_thunk",
  async ({ paginationObj }, thunkAPI) => {
    const config = createGraphRequestConfig(
      "Fetched detail data successfully.",
      "Failed to fetch detail data!",
    );
    const query = buildDetailQueryString(paginationObj);

    const { successResult } = await runDrilldownRequest({
      endpoint: `detail${query}`,
      payload: buildDrilldownPayload(),
      config,
    });

    resetApplyButtonState(thunkAPI);
    return {
      ...successResult,
      paginationObj,
    };
  },
);

export const exportFilteredDrilldownDataThunk = createThunkWithLoader(
  "export_filtered_drilldown_data",
  async () => {
    const config = createGraphRequestConfig();
    const { successResult } = await runDrilldownRequest({
      endpoint: "export?offset=0&limit=0",
      payload: buildDrilldownPayload({
        filter: {
          ...getOnlyIdsForAPI(),
          username: _userMailId(),
        },
      }),
      config,
    });

    const fileUrl = successResult?.data;
    const fileName = _currentDashboard()?.KEY === 2
        ? _selectedCard()?.commercial_title
        : _selectedCard()?.retail_title;
    const fileResponse = await globalThis.fetch(fileUrl);
    const blob = await fileResponse.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName} export data.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    return successResult;
  },
  {
    setLoadingTrue: () => setPerformaLoading(true),
    setLoadingFalse: () => setPerformaLoading(false),
  },
);

export const newClusterSlice = createSlice({
  name: "new_cluster_slice",
  initialState,
  reducers: {
    setSelectedFilterData: (state, action) => {
      state.selectedFilterData = {
        ...state.selectedFilterData,
        [action.payload.storeInKey]: action?.payload?.data,
      };
    },
    setReplaceAllFilterData: (state, action) => {
      const _data = {
        ...action?.payload?.data,
        filterId: action?.payload?.filterId,
      };
      state.selectedFilterData = _data;
    },
    setContextualFilterObj: (state, action) => {
      state.contextualFilterObj = action?.payload;
    },
    updateSortParameters: (state, action) => {
      const {
        clusterApiKey,
        filter,
        sortOrder,
        sortBy,
        subFilterSortOrder,
        subFilterSortBy,
        isSubFilterSortEvent,
      } = action.payload;
      if (clusterApiKey == null) {
        return;
      }
      mapAndAssignFilterGroup(state, clusterApiKey, (item) => {
        if (item.key !== filter) {
          return { ...item };
        }

        if (isSubFilterSortEvent) {
          return { ...item, subFilterSortOrder, subFilterSortBy };
        }

        return { ...item, sortOrder, sortBy };
      });
    },
    updateExpendedMenu: (state, action) => {
      const { filterKey, storeInKey, selectedCheckboxId, extendMenu } =
        action.payload;

      if (filterKey == null) {
        return;
      }

      mapAndAssignFilterGroup(state, filterKey, (item) => {
        if (item.subFilterKey != null && item.key === storeInKey) {
          return { ...item, extendMenu, expendMenuId: selectedCheckboxId };
        }

        return { ...item };
      });
    },
    resetSelectedFilterData: (state) => {
      state.selectedFilterData = {};
      state.contextualFilterObj = null;
    },
    resetNewClusterReducerState: () => initialState,
    setSelectedZones: (state, action) => {
      state.selectedZones = action.payload;
    },
    setSelectedFilterObject: (state, action) => {
      state.selectedFilterObject = action.payload;
    },
    setSimpleState: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
  },

  extraReducers: (builder) => {
    addBasicThunkCases(
      builder,
      fetchFilterApiThunk,
      "initialFilterData",
      (state, action) => {
        const transformedData = transformFilterData(action?.payload?.data, {
          filters: state.FILTERS,
          clusterApiKey: action?.payload?.clusterApiKey,
        });
        setLoadedState(state, "initialFilterData", transformedData);
      },
    );

    addBasicThunkCases(builder, fetchGraphApiThunk, "graphData");

    builder
      .addCase(fetchDetailApiThunk.pending, (_state) => ({
        ..._state,
        detailData: {
          ..._state.detailData,
          detailDataIsLoading: API_STATUS.STATUS_LOADING,
        },
      }))
      .addCase(fetchDetailApiThunk.fulfilled, (_state, _action) => {
        const { currentPage, limit } = _action?.payload?.paginationObj || {};
        const offset = (currentPage - 1) * limit;
        const detailViewData = _action?.payload?.data?.map((item, index) => {
          return { ...item, srNo: offset + index + 1 };
        });
        return {
          ..._state,
          detailData: {
            data: detailViewData,
            totalCount: _action?.payload?.data?.[0]?.totalcount,
            detailDataIsLoading: API_STATUS.STATUS_SUCCESS,
          },
        };
      })
      .addCase(fetchDetailApiThunk.rejected, (_state, _action) => ({
        ..._state,
        detailData: {
          detailDataIsLoading: API_STATUS.STATUS_FAILURE,
          data: [],
          message:
            _action.error.message || "Error while fetching email campaigns",
        },
      }));
  },
});

export const {
  setSelectedFilterData,
  setReplaceAllFilterData,
  setContextualFilterObj,
  updateSortParameters,
  updateExpendedMenu,
  setSelectedZones,
  setSelectedFilterObject,
  resetSelectedFilterData,
  resetNewClusterReducerState,
} = newClusterSlice.actions;

export default newClusterSlice.reducer;
